import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Search, SlidersHorizontal } from 'lucide-react'
import { listInvoices } from '../../api/purchase'
import { getConfig, saveConfig } from '../../api/settings'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { HelpField, HelpTip } from '../../components/HelpTip'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay, parseWallClock } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import AccountingTabs from './AccountingTabs'

// Accounting's steps for a vendor invoice, after Purchasing sends it.
const STEPS = {
  Submitted: { show: 'check', label: 'To check', tone: 'info' },
  MatchException: { show: 'check', label: 'Doesn’t match', tone: 'warn' },
  Matched: { show: 'approve', label: 'To approve', tone: 'info' },
  Approved: { show: 'post', label: 'To post', tone: 'info' },
  PostedToAP: { show: 'posted', label: 'Posted · unpaid', tone: 'good' },
  PartiallyPaid: { show: 'posted', label: 'Posted · part paid', tone: 'good' },
  Paid: { show: 'posted', label: 'Paid', tone: 'good' },
  ReturnedToPurchasing: { show: 'other', label: 'Sent back', tone: 'muted' },
  Rejected: { show: 'other', label: 'Rejected', tone: 'bad' },
  Voided: { show: 'other', label: 'Void', tone: 'bad' },
}
const SHOW = [
  { id: 'check', label: 'To check' },
  { id: 'approve', label: 'To approve' },
  { id: 'post', label: 'To post' },
  { id: 'posted', label: 'Posted' },
  { id: 'other', label: 'Sent back or rejected' },
  { id: 'all', label: 'All' },
]
// Older links used ?filter=pending|exceptions|matched.
const OLD = { pending: 'check', exceptions: 'check', matched: 'posted' }

const HELP = 'Purchasing enters vendor bills and sends them here. Accounting checks each one against its purchase order and goods receipt (a three-way match), approves it, then posts it to accounts payable, where it waits to be paid.'

// Vendor invoices waiting on Accounting: check, approve, post.
export default function VendorInvoiceMatching() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const asked = params.get('show') || OLD[params.get('filter')]
  const [rows, setRows] = useState(null)
  const [search, setSearch] = useState('')
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEffect(() => {
    listInvoices()
      .then((d) => setRows((Array.isArray(d) ? d : []).filter((r) => STEPS[r.status])))
      .catch(() => setRows([]))
  }, [])

  const counts = useMemo(() => Object.fromEntries(SHOW.map((s) => [s.id, (rows || []).filter((r) => s.id === 'all' || STEPS[r.status].show === s.id).length])), [rows])
  // Without a choice in the address, open on the first step with work in it.
  const show = SHOW.some((s) => s.id === asked) ? asked : (['check', 'approve', 'post'].find((id) => counts[id] > 0) || 'all')
  const setShow = (id) => setParams(id === 'all' && !asked ? {} : { show: id }, { replace: true })

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (rows || [])
      .filter((r) => (show === 'all' || STEPS[r.status].show === show)
        && (!term || [r.invoice_number, docNo('bill', r.bill_no), r.vendor_name, docNo('po', r.po_no)].join(' ').toLowerCase().includes(term)))
      .sort((a, b) => (parseWallClock(b.invoice_date)?.getTime() || 0) - (parseWallClock(a.invoice_date)?.getTime() || 0) || num(b.invoice_id) - num(a.invoice_id))
  }, [rows, show, search])

  return (
    <div className="ci-page">
      <AccountingTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Vendor invoices <HelpTip text={HELP} /></h1>
            <div className="ci-title-meta">Check, approve and post vendor bills.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button data-write type="button" className="ci-btn ci-btn-ghost" onClick={() => setSettingsOpen(true)}><SlidersHorizontal size={15} />Matching tolerances</button>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search vendor invoices">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Invoice number, vendor or PO…" />
        </label>
      </div>

      <FilterChips options={SHOW.map((s) => ({ ...s, count: counts[s.id] }))} value={show} onChange={setShow} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Vendor</th>
                <th className="hide-sm">Date</th>
                <th className="hide-sm">For</th>
                <th className="center">Status</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                <tr><td colSpan="6"><Spinner label="Loading vendor invoices…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{rows.length ? 'Nothing here.' : 'No vendor invoices have been sent to Accounting.'}</div></td></tr>
              ) : shown.map((r) => {
                const st = STEPS[r.status]
                return (
                  <tr key={r.invoice_id} className="row-link" onClick={() => nav(`/accounting/ap/invoices/${r.invoice_id}`)}>
                    <td><Link className="ci-je-link" to={`/accounting/ap/invoices/${r.invoice_id}`} onClick={(e) => e.stopPropagation()}>{r.invoice_number || docNo('bill', r.bill_no)}</Link></td>
                    <td className="ci-strong">{r.vendor_name || '–'}</td>
                    <td className="ci-muted-cell hide-sm">{fmtDay(r.invoice_date) || '–'}</td>
                    <td className="hide-sm">{r.invoice_type === 'NonPO' ? 'Services' : (r.po_id ? `Products · ${docNo('po', r.po_no)}` : 'Products')}</td>
                    <td className="center"><StatusBadge label={st.label} tone={st.tone} /></td>
                    <td className="num ci-total">{money(r.total_amount)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {shown.length === 1 ? 'invoice' : 'invoices'} · {money(shown.reduce((s, r) => s + num(r.total_amount), 0))}</span>
        </div>
      </div>

      <TolerancesDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  )
}

function TolerancesDialog({ open, onClose }) {
  const { notify } = useToast()
  const [price, setPrice] = useState('0')
  const [qty, setQty] = useState('0')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setError('')
    getConfig().then((c) => {
      setPrice(String(c.invoice_match_price_tolerance_pct ?? 0))
      setQty(String(c.invoice_match_qty_tolerance_pct ?? 0))
    }).catch(() => {})
  }, [open])

  const save = async () => {
    const p = Number(price)
    const q = Number(qty)
    if (!(p >= 0) || !(q >= 0)) { setError('Enter a percentage of 0 or more.'); return }
    setSaving(true)
    setError('')
    try {
      await saveConfig({ invoice_match_price_tolerance_pct: p, invoice_match_qty_tolerance_pct: q })
      notify('Matching tolerances saved')
      onClose()
    } catch (e) {
      setError(e.message || 'Could not save the tolerances.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Matching tolerances"
      subtitle="How far an invoice may differ from its purchase order and receipt and still match."
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={save}>Save</Button>
        </>
      )}
    >
      {error && <ErrorBanner message={error} />}
      <div className="acc-two-fields">
        <HelpField label="Price difference (%)" help="An invoice price this much above or below the PO price still matches. 0 means it must be exact.">
          <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" />
        </HelpField>
        <HelpField label="Quantity difference (%)" help="An invoiced quantity this much above or below what was received still matches. 0 means it must be exact.">
          <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" />
        </HelpField>
      </div>
    </Modal>
  )
}
